import React, { useEffect, useState } from "react";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { createPaymentIntent } from "../api";
import "./PaymentForm.css";

/**
 * Le montant réellement débité est calculé par la Cloud Function à partir des
 * adresses : rien de ce qui vient du navigateur n'est utilisé comme prix.
 * `estimate` n'est affiché que le temps que le serveur réponde.
 */

const CARD_STYLE = {
  style: {
    base: {
      fontSize: "14px",
      color: "#241535",
      fontFamily: "Poppins, sans-serif",
      "::placeholder": { color: "#8b8296" },
    },
    invalid: { color: "#c8385a" },
  },
};

const PaymentForm = ({ trip, estimate }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [intent, setIntent] = useState(null); // { clientSecret, amount }
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await createPaymentIntent(trip);
        if (!cancelled) setIntent(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Le trajet est figé au moment où l'on ouvre le paiement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !intent) return;

    setProcessing(true);
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
      intent.clientSecret,
      {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email: trip.email },
        },
      }
    );

    if (stripeError) {
      setError(`Paiement refusé : ${stripeError.message}`);
    } else {
      setReceipt({ amount: paymentIntent.amount / 100 });
    }
    setProcessing(false);
  };

  if (receipt) {
    return (
      <div className="payment-box payment-success">
        <h3>Réservation confirmée</h3>
        <p>
          Paiement de {receipt.amount} € accepté pour le {trip.date} à {trip.time}.
        </p>
        <p>Un reçu et la confirmation partent à l'adresse {trip.email}.</p>
      </div>
    );
  }

  const amount = intent?.amount ?? estimate;

  return (
    <form className="payment-box" onSubmit={handleSubmit}>
      {intent ? (
        <div className="payment-breakdown">
          <div className="payment-breakdown-row">
            <span>Tarif de base ({intent.pricePerKm} €/km)</span>
            <span>{intent.basePrice} €</span>
          </div>

          {intent.surcharge.percent > 0 && (
            <div className="payment-breakdown-row payment-breakdown-surcharge">
              <span>
                {intent.surcharge.label} (+{intent.surcharge.percent}%)
              </span>
              <span>+{intent.surcharge.amount} €</span>
            </div>
          )}

          <div className="payment-breakdown-row payment-breakdown-total">
            <span>Total à régler</span>
            <strong>{amount} €</strong>
          </div>
        </div>
      ) : (
        <div className="payment-amount">
          <span>Montant à régler</span>
          <strong>{amount} €</strong>
        </div>
      )}

      {intent && intent.amount !== estimate && (
        <p className="payment-note">
          Montant recalculé par nos serveurs à partir de l'itinéraire et de l'horaire réels.
        </p>
      )}

      <label className="payment-field">
        <span>Numéro de carte</span>
        <div className="payment-card-input">
          <CardElement options={CARD_STYLE} />
        </div>
      </label>

      {error && <p className="form-error">{error}</p>}

      <button
        type="submit"
        className="btn btn-primary btn-block"
        disabled={!stripe || processing || !intent}
      >
        {processing ? "Traitement…" : intent ? `Payer ${amount} €` : "Préparation…"}
      </button>
    </form>
  );
};

export default PaymentForm;
